import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import {
  Card,
  CardContent,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  CircularProgress,
  Grid,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { setLoginUser } from '../../../hooks/loginInfo';
import { setAuth } from '../../../hooks/auth';
import { saveRefreshToken, saveToken } from '../../../helper/auth';
import config from '../../../config/config';
import { SocialAuthProvider, useTokenAuthMutation } from '../../../../graphql/generated/graphql';
import OnBoardingLayout from '../../../theme/templates/on-boarding';
import jwtDecode from 'jwt-decode';

export default function LoginPage() {
  const navigate = useNavigate();
  const [loginMutation] = useTokenAuthMutation();
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showError, setShowError] = useState(false);

  const { VERIFY_MESSAGE } = config;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoader(true);
    const data = new FormData(event.currentTarget);
    try {
      const response = await loginMutation({
        variables: {
          email_or_phone: data.get('user') as string,
          password: data.get('password') as string,
        },
      });
      setLoginUser(response.data?.tokenAuth?.payload);
      saveToken(response.data?.tokenAuth?.token as string);
      saveRefreshToken(response.data?.tokenAuth?.refresh_token as string);
      setAuth(true);
    } catch (e: any) {
      if (e.message === VERIFY_MESSAGE) {
        navigate('/verify-email');
      } else {
        setShowError(true);
      }
    }
    setLoader(false);
  };

  const responseGoogle = (response: CredentialResponse) => {
    const userObject: any = jwtDecode(response?.credential as string);
    try {
      const getInfouser = async () => {
        const res2 = await loginMutation({
          variables: {
            email_or_phone: userObject?.email as string,
            socialAuthProvider: SocialAuthProvider.Google,
            tokenID: response.credential
          },
        });
        if (res2.data?.tokenAuth?.refresh_token && res2.data?.tokenAuth?.token) {
          setLoginUser(res2.data?.tokenAuth?.payload);
          saveToken(res2.data?.tokenAuth?.token as string);
          saveRefreshToken(res2.data?.tokenAuth?.refresh_token as string);
          setAuth(true);
        }
        else {
          setLoginUser(res2.data?.tokenAuth?.payload);
          navigate('/register-google')
        }
      };
      getInfouser();
    } catch (error) {
      console.log(error)
    }
  }

  const errorResponse = () => {
    console.log('Login Failed!')
  }

  return (
    <OnBoardingLayout>
      <Card
        sx={{
          maxWidth: '90%',
          width: '500px',
          mb: 2,
          mx: 4,
          py: 2,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography component="h1" variant="h3">
          Sign in
        </Typography>
        <CardContent component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="user"
            label="Email or Phone"
            name="user"
            autoComplete="user"
            autoFocus
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={<Checkbox value="remember" color="primary" size="small" />}
            label="Remember me"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 2, mb: 2 }}
          >
            {loader ? <CircularProgress color="primary" /> : 'Sign In'}
          </Button>
          <Grid container sx={{ mt: 1 }}>
            <Grid
              item
              sm={4}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}>
              <hr style={{ borderTop: '1px solid lightgray' }} />
            </Grid>
            <Grid item sm={4} sx={{ textAlign: 'center' }}>
              OR
            </Grid>
            <Grid
              item
              sm={4}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <hr style={{ borderTop: '1px solid lightgray' }} />
            </Grid>
          </Grid>
          <Grid container sx={{ mt: 1 }}>
            <Grid item sx={{ mx: 'auto' }}>
              <Typography
                component='p'
                variant='body1'
                sx={{
                  fontSize: '12px'
                }}
              >
                Log in with
              </Typography>
            </Grid>
          </Grid>
          <Grid container sx={{ mt: 2 }}>
            <Grid item sx={{ mx: 'auto' }}>
              <GoogleLogin
                onSuccess={responseGoogle}
                onError={errorResponse}
              />
            </Grid>
          </Grid>
          <Grid container sx={{ mt: 2 }}>
            <Grid item sx={{ mx: 'auto' }}>
              <Link href="/forget" variant="caption">
                Forgot password?
              </Link>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Card
        sx={{
          maxWidth: '90%',
          width: '500px',
          mt: 2,
          mb: 4,
          mx: 4,
          py: 2,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CardContent component="div" sx={{ mt: 1 }}>
          <Grid item>
            Don&apos;t have an account?&nbsp;
            <Link href="/register" variant="body2">
              Sign Up
            </Link>
          </Grid>
        </CardContent>
      </Card>
    </OnBoardingLayout>
  )
}
